import React from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentTime } from '@/hooks/useAstronomicalData';
import MoonPhaseVisual from '@/components/MoonPhaseVisual';
import AnalogClock from '@/components/AnalogClock';
import { localizeMoonPhase } from '@/utils/localizedStrings';

/**
 * Get locale string with:
 *   - Latin numerals (so digits stay 0-9 not ٠-٩)
 *   - Gregorian calendar (ar-SA defaults to Hijri otherwise!)
 */
const getLocale = (lang) => {
  if (lang === 'ar') return 'ar-SA-u-ca-gregory-nu-latn';
  if (lang === 'id') return 'id-ID';
  if (lang === 'ur') return 'ur-PK-u-ca-gregory-nu-latn';
  if (lang === 'fr') return 'fr-FR';
  if (lang === 'bn') return 'bn-BD-u-nu-latn';
  if (lang === 'zh') return 'zh-CN';
  if (lang === 'fa') return 'fa-IR-u-ca-gregory-nu-latn';
  if (lang === 'tr') return 'tr-TR';
  if (lang === 'es') return 'es-ES';
  if (lang === 'pt') return 'pt-PT';
  return 'en-US';
};

// Hijri date suffix/separator per language
const HIJRI_SUFFIX = {
  ar: 'هـ', en: 'AH', id: 'H', ur: 'ہجری', fr: 'AH',
  bn: 'হিজরি', zh: '希吉来', fa: 'ه‍.ق', tr: 'H.', es: 'AH', pt: 'AH'
};

/**
 * UnifiedHeroCard - merged display of:
 *   - Digital/Analog clock (top, headline)
 *   - Hijri date
 *   - Gregorian date
 *   - Moon visual
 *   - Moon info (phase name, illumination, age, visibility)
 *
 * Avoids any duplicate information.
 */
const UnifiedHeroCard = ({
  moonPhase,
  hijriDate,
  surfaceColors,
  textColors,
  clockType = 'digital'
}) => {
  const { t, i18n } = useTranslation();
  const currentTime = useCurrentTime();
  const lang = i18n.language;
  const isRTL = ['ar', 'ur', 'fa'].includes(lang);
  const locale = getLocale(lang);

  // Time components
  const h = String(currentTime.getHours()).padStart(2, '0');
  const m = String(currentTime.getMinutes()).padStart(2, '0');
  const s = String(currentTime.getSeconds()).padStart(2, '0');

  // Gregorian date — WITHOUT the weekday (day name is already shown with
  // the Hijri line above; repeating it here is visual noise).
  const gregorianDate = currentTime.toLocaleDateString(locale, {
    year: 'numeric', month: 'long', day: 'numeric'
  });

  // Localized weekday name — derived from the browser so every one of the
  // 11 UI languages gets a native day name automatically. Placed on the
  // Hijri line so we never repeat it on the Gregorian line below.
  const weekdayName = currentTime.toLocaleDateString(locale, { weekday: 'long' });

  // Hijri date string - language-aware
  const buildHijriString = () => {
    if (!hijriDate) return null;
    const suffix = HIJRI_SUFFIX[lang] || HIJRI_SUFFIX.en;
    if (lang === 'ar') {
      return `${weekdayName}، ${hijriDate.day} ${hijriDate.month_name_ar} ${hijriDate.year} ${suffix}`;
    }
    if (lang === 'ur') {
      return `${weekdayName}، ${hijriDate.day} ${hijriDate.month_name_en} ${hijriDate.year} ${suffix}`;
    }
    // Default for en, id, fr, bn, zh, fa, tr, es, pt: english month + lang suffix
    return `${weekdayName}, ${hijriDate.day} ${hijriDate.month_name_en} ${hijriDate.year} ${suffix}`;
  };
  const hijriString = buildHijriString();

  return (
    <div
      className="rounded-3xl backdrop-blur-2xl border glass-surface overflow-hidden"
      style={{
        background: surfaceColors.background,
        borderColor: surfaceColors.border
      }}
      data-testid="unified-hero-card"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="p-6 md:p-10 flex flex-col items-center text-center">

        {/* === CLOCK (Top, headline) === */}
        {clockType === 'analog' ? (
          <div className="w-full max-w-md mb-6">
            <AnalogClock textColors={textColors} surfaceColors={surfaceColors} />
          </div>
        ) : (
          <div
            className="clock-display flex items-center gap-1 md:gap-3 mb-4"
            style={{
              fontSize: 'clamp(3.5rem, 14vw, 9.5rem)',
              color: textColors.primary,
              textShadow: '0 2px 30px rgba(0, 0, 0, 0.3)',
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums'
            }}
            dir="ltr"
            data-testid="hero-clock"
          >
            <span
              data-testid="hours"
              style={{ display: 'inline-block', width: '1.2em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
            >
              {h}
            </span>
            <span className="clock-colon" style={{ opacity: 0.6, display: 'inline-block', width: '0.35em', textAlign: 'center' }}>:</span>
            <span
              data-testid="minutes"
              style={{ display: 'inline-block', width: '1.2em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
            >
              {m}
            </span>
            <span className="clock-colon" style={{ opacity: 0.6, display: 'inline-block', width: '0.35em', textAlign: 'center' }}>:</span>
            <span
              data-testid="seconds"
              className="text-[0.6em]"
              style={{ opacity: 0.7, display: 'inline-block', width: '1.2em', textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
            >
              {s}
            </span>
          </div>
        )}

        {/* === HIJRI DATE === */}
        {hijriString && (
          <div
            className="text-body mb-1"
            style={{
              color: textColors.primary,
              fontSize: 'clamp(1rem, 1.5vw, 1.65rem)'
            }}
            data-testid="hero-hijri-date"
          >
            {hijriString}
          </div>
        )}

        {/* === GREGORIAN DATE === */}
        <div
          className="text-body mb-8"
          style={{
            color: textColors.primary,
            fontSize: 'clamp(1rem, 1.5vw, 1.65rem)'
          }}
          data-testid="hero-gregorian-date"
        >
          {gregorianDate}
        </div>

        {/* === MOON VISUAL + INFO === */}
        {moonPhase && (
          <div className="w-full flex flex-col md:flex-row items-center justify-center gap-6 md:gap-10 pt-6 border-t" style={{ borderColor: surfaceColors.border }}>
            <MoonPhaseVisual
              illumination={moonPhase.illumination}
              ageDays={moonPhase.age_days}
              size={140}
            />

            <div className="flex flex-col gap-2 text-center md:text-start min-w-[180px]">
              <div
                className="text-heading text-lg md:text-xl"
                style={{ color: textColors.primary }}
                data-testid="hero-moon-phase-name"
              >
                {localizeMoonPhase(moonPhase.phase_name, lang)}
              </div>

              <div className="flex justify-between gap-6 text-sm text-body" style={{ color: textColors.secondary }}>
                <span>{t('moonIllumination')}</span>
                <span className="tabular-nums mono-text" style={{ color: textColors.primary }} data-testid="hero-illumination">
                  {(moonPhase.illumination * 100).toFixed(1)}%
                </span>
              </div>

              <div className="flex justify-between gap-6 text-sm text-body" style={{ color: textColors.secondary }}>
                <span>{t('moonAge')}</span>
                <span className="tabular-nums mono-text" style={{ color: textColors.primary }} data-testid="hero-moon-age">
                  {moonPhase.age_days.toFixed(1)} {t('days')}
                </span>
              </div>

              {moonPhase.is_visible && (
                <div className="mt-1 inline-flex items-center justify-center md:justify-start gap-1.5">
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: textColors.accent }}
                  />
                  <span
                    className="text-[10px] text-caption"
                    style={{ color: textColors.accent }}
                  >
                    {t('visible')}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedHeroCard;
